/**
 * ViewAppBar — Topbar moderna (alinhada com o splash inicial).
 *
 * Visual:
 *   - Gradient azul diagonal (mesmo do OpeningAnimation/splash)
 *   - Overlays radiais sutis pra dar profundidade
 *   - Título sem caps lock (weight 600)
 *   - Sombra suave multinível
 *   - LinearProgress slim com gradient idêntico ao do splash
 */
import React from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  IconButton,
  LinearProgress,
} from '@mui/material';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Close, Menu as MenuIcon } from '@mui/icons-material';
import BashboardContext from '../../components/Dashboard/DasboardContext';
import { createSetObject } from '../../core/util';
import qcomm from '../../core/comm/queries';

import withWidth from '../../components/useWidth';

const smallSizes = createSetObject('xs', 'sm', 'md');

function ViewAppBar(props) {
  const {
    title,
    width,
    history,
    className,
    hideTitle,
    actionPanel,
    isUploading,
    closeButton,
    isDownloading,
    hideToggleMenuButton,
  } = props;
  const { toggleDrawer } = React.useContext(BashboardContext);
  const isLargeScreen = width === 'lg' || width === 'xl';
  const showToggleButton = !hideToggleMenuButton && !isLargeScreen;
  const isLoading = isDownloading || isUploading;

  // Calibra fonte do título conforme largura/tamanho do texto (mobile).
  const titleFontSize = (() => {
    if (width !== 'xs') return { xs: '1.05rem', sm: '1.15rem', md: '1.2rem' };
    if (title && title.length > 16) return '0.85rem';
    if (title && title.length > 12) return '0.95rem';
    return '1.05rem';
  })();

  return (
    <>
      <AppBar
        elevation={0}
        position="static"
        className={className}
        sx={{
          position: 'relative',
          // Fundo neutro alinhado com a tela (branco com leve respiro).
          // Só os botões e ações ficam em azul — sidebar continua azul gradient.
          backgroundColor: '#ffffff',
          color: '#1a3a47',
          borderBottom: '1px solid rgba(0, 128, 173, 0.10)',
          boxShadow: 'none',
        }}
      >
        <Toolbar
          disableGutters={smallSizes[width]}
          sx={{
            minHeight: { xs: 56, md: 64 },
            px: { xs: 1, sm: 2, md: 3 },
          }}
        >
          {showToggleButton && (
            <IconButton
              onClick={toggleDrawer}
              size="large"
              sx={{
                mr: 1,
                color: '#0080ad',
                bgcolor: 'rgba(0, 128, 173, 0.08)',
                borderRadius: '10px',
                p: 1,
                '&:hover': { bgcolor: 'rgba(0, 128, 173, 0.14)' },
              }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'hidden',
              display: hideTitle ? 'none' : 'flex',
              alignItems: 'center',
              transition: 'opacity 0.2s ease',
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Montserrat", system-ui, sans-serif',
                color: '#1a3a47',
                fontWeight: 600,
                letterSpacing: '-0.2px',
                fontSize: titleFontSize,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                pl: !showToggleButton ? { xs: 1, md: 0 } : 0,
              }}
            >
              {title}
            </Typography>
          </Box>
          {closeButton && (
            <IconButton
              onClick={() => history.goBack()}
              size="large"
              sx={{
                color: '#0080ad',
                bgcolor: 'rgba(0, 128, 173, 0.08)',
                borderRadius: '10px',
                p: 1,
                mr: 0.5,
                '&:hover': { bgcolor: 'rgba(0, 128, 173, 0.14)' },
              }}
            >
              <Close />
            </IconButton>
          )}
          {actionPanel}
        </Toolbar>
      </AppBar>

      {/* LinearProgress slim com gradient do splash. Mantém altura 4px
          quando inativo pra evitar pulo de layout. */}
      {isLoading ? (
        <Box
          sx={{
            height: 4,
            position: 'relative',
            overflow: 'hidden',
            bgcolor: 'rgba(0, 128, 173, 0.10)',
          }}
        >
          <LinearProgress
            sx={{
              height: 4,
              bgcolor: 'transparent',
              '& .MuiLinearProgress-bar': {
                background:
                  'linear-gradient(90deg, #09AFDF 0%, #0080ad 100%)',
              },
            }}
          />
        </Box>
      ) : (
        <Box sx={{ height: 4 }} />
      )}
    </>
  );
}

function mapStateToProps(state) {
  return {
    isDownloading: qcomm.isLoading(state),
    isUploading: qcomm.isSending(state),
  };
}

export default connect(mapStateToProps, null)(withRouter(withWidth()(ViewAppBar)));
